/*
 * Fit finder — W2-FINDER
 * Progressive-enhancement search over a fixed set of audience-page cards.
 * All copy and synonym lists are supplied server-side (already localized) via a
 * per-instance JSON blob, so this file carries no copy and no per-locale logic.
 * Baseline (no JS): every existing-page card is already visible in the DOM as a
 * plain link, chips are plain anchors to their card, and the search input sits in
 * a real GET form. This script only enhances that baseline; it never removes the
 * only way to reach a card.
 */
(function () {
  'use strict';

  // Small, deliberately English-only list of connector words that show up
  // inside multi-word synonym phrases (e.g. "couples and families"). Without
  // this, a query token like "and" scores a match against every such phrase.
  // Kept short on purpose: this only needs to blunt the handful of function
  // words that actually appear in the shipped synonym lists, not act as a
  // general-purpose stopword list for every locale.
  var STOPWORDS = {
    'and': 1, 'a': 1, 'the': 1, 'to': 1, 'of': 1, 'in': 1, 'on': 1,
    'for': 1, 'with': 1, 'or': 1, 'is': 1, 'my': 1, 'me': 1, 'i': 1, 'at': 1
  };

  // NFKD decomposes most accented letters (é -> e + combining acute), which
  // the regex above strips. A handful of letters used in real DA/NO/TR/MT
  // synonym lists do NOT decompose under NFKD (they are atomic code points,
  // not base+combining-mark pairs): æ, ø, dotless i (ı), ħ, plus ß/đ/ł/œ
  // from other locales. Without folding these too, a visitor who spells a
  // word the normal way (e.g. Danish "forældre", Turkish "yaşlı") never
  // matches the synonym lists, which are written in plain ASCII. Folded on
  // both the query and the synonym side, so they always compare equal.
  function normalize(str) {
    return (str || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/æ/g, 'ae')
      .replace(/ø/g, 'o')
      .replace(/ı/g, 'i')
      .replace(/ħ/g, 'h')
      .replace(/ß/g, 'ss')
      .replace(/đ/g, 'd')
      .replace(/ł/g, 'l')
      .replace(/œ/g, 'oe')
      .trim();
  }

  // Unicode-aware split: \p{L}/\p{N} cover letters and numbers in every
  // script (Greek, Cyrillic, Latin with diacritics, etc.), not just a-z0-9.
  function tokenize(str) {
    return normalize(str)
      .split(/[^\p{L}\p{N}]+/u)
      .filter(Boolean);
  }

  function significantTokens(tokens) {
    return tokens.filter(function (t) {
      return t.length > 1 && !STOPWORDS[t];
    });
  }

  // Token-exact matching only — never substring/indexOf. Substring matching
  // on whole strings caused false positives like "mum" inside "maximum" or
  // "os" inside "dos"/"gros"/"chose"; comparing whole tokens is script-safe
  // (no \b regex needed, which would fail on Greek/Cyrillic anyway) and
  // eliminates that class of bug entirely.
  //
  // Partial credit (the "else" branch below) requires at least 2 overlapping
  // tokens, not 1. A single shared word between the query and a multi-word
  // synonym phrase produced confident wrong answers: "no room" matched Busy
  // parents via the lone word "no" (shared with its "no time" synonym), and
  // "first time" matched the same card via the lone word "time" (shared with
  // "nap time"), even though neither query has anything to do with parents.
  // A 1-word overlap on an otherwise-unrelated multi-word phrase is noise;
  // requiring 2 keeps genuine partial matches (e.g. two of three words in a
  // longer phrase) while dropping single generic-word coincidences. A single
  // significant word still fully matches whenever it IS a synonym in its own
  // right (the "allPresent" branch above, unaffected by this threshold).
  function scoreCard(queryTokens, card) {
    if (!queryTokens.length) return 0;
    var qSet = {};
    for (var i = 0; i < queryTokens.length; i++) qSet[queryTokens[i]] = true;

    var score = 0;
    var synonyms = card.synonyms || [];
    for (var s = 0; s < synonyms.length; s++) {
      var synTokens = significantTokens(tokenize(synonyms[s]));
      if (!synTokens.length) continue;
      var allPresent = true;
      var overlap = 0;
      for (var j = 0; j < synTokens.length; j++) {
        if (qSet[synTokens[j]]) {
          overlap += 1;
        } else {
          allPresent = false;
        }
      }
      if (allPresent) {
        score += 3;
      } else if (overlap >= 2) {
        score += overlap;
      }
    }
    return score;
  }

  // Fallback-only ranking, never used to decide whether something "matched"
  // (scoreCard above is the only gate for that). When nothing clears
  // scoreCard's bar, the "closest fits" fallback used to just show the
  // first 3 cards in a fixed default order, so an unmatched query always
  // showed the same three pages regardless of what was typed, and could
  // never surface Small homes or Starting strength training. This scores
  // every card by loose token overlap (a shared word, worth 2) plus a
  // 4-character prefix match on words that aren't shared outright (worth 1,
  // e.g. "trained" vs a synonym's "training"), so closest-3 is ranked by
  // actual similarity to the query instead of array position.
  function closenessScore(queryTokens, card) {
    if (!queryTokens.length) return 0;
    var qSet = {};
    for (var i = 0; i < queryTokens.length; i++) qSet[queryTokens[i]] = true;

    var score = 0;
    var synonyms = card.synonyms || [];
    for (var s = 0; s < synonyms.length; s++) {
      var synTokens = significantTokens(tokenize(synonyms[s]));
      for (var j = 0; j < synTokens.length; j++) {
        var t = synTokens[j];
        if (qSet[t]) {
          score += 2;
          continue;
        }
        for (var k = 0; k < queryTokens.length; k++) {
          var qt = queryTokens[k];
          if (qt.length >= 4 && t.length >= 4 && qt.slice(0, 4) === t.slice(0, 4)) {
            score += 1;
            break;
          }
        }
      }
    }
    return score;
  }

  function FitFinder(root) {
    this.root = root;
    this.dataEl = root.querySelector('[data-bm-finder-data]');
    this.cards = {};
    this.defaultOrder = [];
    try {
      var parsed = JSON.parse(this.dataEl.textContent);
      this.copy = parsed.copy || {};
      var list = parsed.cards || [];
      for (var i = 0; i < list.length; i++) {
        this.cards[list[i].handle] = list[i];
        this.defaultOrder.push(list[i].handle);
      }
    } catch (e) {
      this.copy = {};
    }

    this.form = root.querySelector('[data-bm-finder-form]');
    this.input = root.querySelector('[data-bm-finder-input]');
    this.status = root.querySelector('[data-bm-finder-status]');
    this.chips = Array.prototype.slice.call(root.querySelectorAll('[data-bm-finder-chip]'));
    this.cardEls = {};
    Array.prototype.slice.call(root.querySelectorAll('[data-bm-finder-card]')).forEach(function (el) {
      this.cardEls[el.getAttribute('data-bm-finder-card')] = el;
    }, this);
    this.q1 = root.querySelector('[data-bm-finder-q1]');
    this.q2 = root.querySelector('[data-bm-finder-q2]');
    this.pickerForm = root.querySelector('[data-bm-finder-picker-form]');
    this.grid = root.querySelector('[data-bm-finder-cardlist]');
    this._debounceTimer = null;

    this.bind();
  }

  FitFinder.prototype.cancelPendingSearch = function () {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
  };

  FitFinder.prototype.bind = function () {
    var self = this;

    if (this.form && this.input) {
      // Prevent the default full-page GET submit once JS can handle it live;
      // the form still works as a plain GET to /search if JS fails to attach.
      // Never auto-scroll from the search box: a debounced keystroke used to
      // call the same scrollIntoView as a chip/picker action, which yanked
      // the page (and the on-screen keyboard) out from under a still-typing
      // visitor on mobile. Typed search only ever updates the status line
      // and the grid in place.
      this.form.addEventListener('submit', function (evt) {
        evt.preventDefault();
        self.cancelPendingSearch();
        self.runQuery(self.input.value, false);
      });
      this.input.addEventListener('input', function () {
        self.cancelPendingSearch();
        self._debounceTimer = setTimeout(function () {
          self._debounceTimer = null;
          self.runQuery(self.input.value, false);
        }, 200);
      });
    }

    this.chips.forEach(function (chip) {
      chip.addEventListener('click', function (evt) {
        var term = chip.getAttribute('data-bm-finder-chip');
        if (self.input) {
          evt.preventDefault();
          self.cancelPendingSearch();
          self.input.value = term;
          // Deliberately no self.input.focus() here: focusing a text input
          // on tap opens the on-screen keyboard on mobile for an action that
          // never asked the visitor to type. The input's value still updates
          // so the chosen chip is visible in the box.
          self.runQuery(term, true);
          chip.setAttribute('aria-pressed', 'true');
        }
        // If there is no input (compact/links mode has no search box), the
        // chip is a plain anchor to the card and this handler does nothing
        // beyond the default navigation.
      });

      // Full-mode chips carry role="button" (they act on the page in place
      // rather than navigate), and a real <button> activates on both Enter
      // and Space. A plain <a>, even with role="button" layered on, only
      // gets Enter for free from the browser; Space does nothing unless we
      // wire it up ourselves. Compact/links-mode chips have no role="button"
      // (they are genuine links to another page), so they are left alone:
      // real links never expect Space to activate them.
      chip.addEventListener('keydown', function (evt) {
        if (chip.getAttribute('role') !== 'button') return;
        if (evt.key !== ' ' && evt.key !== 'Spacebar') return;
        evt.preventDefault();
        chip.click();
      });
    });

    if (this.pickerForm) {
      this.pickerForm.addEventListener('submit', function (evt) {
        evt.preventDefault();
        self.cancelPendingSearch();
        self.runPicker();
      });
      if (this.q1) this.q1.addEventListener('change', function () { self.cancelPendingSearch(); self.runPicker(); });
      if (this.q2) this.q2.addEventListener('change', function () { self.cancelPendingSearch(); self.runPicker(); });
    }
  };

  FitFinder.prototype.selectedHandles = function (select) {
    if (!select) return [];
    var opt = select.options[select.selectedIndex];
    if (!opt) return [];
    var raw = opt.getAttribute('data-handles');
    if (!raw) return [];
    return raw.split(',').map(function (h) { return h.trim(); }).filter(Boolean);
  };

  // The picker's answers are mapped directly to page handles (via
  // data-handles on each <option>, set server-side) rather than being run
  // back through the fuzzy synonym scorer. Scoring translated option text
  // against English-authored synonym lists produced wrong or no matches in
  // most non-English locales; a direct mapping is correct in every locale
  // by construction and needs no per-language tuning.
  FitFinder.prototype.runPicker = function () {
    // A prior typed search leaves its text in the box; without clearing it,
    // answering the picker afterwards showed the old query still sitting in
    // the input while the grid displayed the picker's own (different)
    // results, which read as a contradiction.
    if (this.input) this.input.value = '';

    // q2 ("what matters most right now") first, q1 ("who is training") second:
    // when both questions map to a different page, the more specific,
    // stated priority should rank first, not whichever question happens to
    // sit first in the form.
    var handles = this.selectedHandles(this.q2).concat(this.selectedHandles(this.q1));
    var seen = {};
    var uniqueHandles = [];
    handles.forEach(function (h) {
      if (h && !seen[h]) { seen[h] = true; uniqueHandles.push(h); }
    });

    var labelParts = [];
    if (this.q1 && this.q1.value) labelParts.push(this.q1.value);
    if (this.q2 && this.q2.value) labelParts.push(this.q2.value);
    var label = labelParts.join(' + ');

    if (!uniqueHandles.length) {
      this.render(this.availableHandles(), { mode: 'all', scroll: true });
      return;
    }

    var available = this.availableHandles();
    var matched = uniqueHandles.filter(function (h) { return available.indexOf(h) !== -1; });
    if (!matched.length) {
      // Neither mapped page exists on this store: never dead-end, show
      // everything that does exist rather than falling back to text
      // scoring (which is exactly the bug this replaces).
      this.render(available, { mode: 'all', scroll: true });
      return;
    }
    // Show only the matched card(s), same as a search-box match: the status
    // line's count ("Results for X: 1") used to be true while all 5 cards
    // stayed visible with the match only moved first, which read as a
    // contradiction. Hiding the rest makes the count match what's on screen.
    this.render(matched, { mode: 'match', query: label, count: matched.length, scroll: true });
  };

  FitFinder.prototype.availableHandles = function () {
    return this.defaultOrder.filter(function (h) {
      return !!this.cardEls[h];
    }, this);
  };

  // `scroll` is true only when this call originated from a chip tap or the
  // picker (see bind()): those are discrete actions where jumping to the
  // results is the expected cue that something changed. It is false for
  // every call that came from the search box itself (typed input and its
  // own form submit), so the results updating in place never moves the
  // page out from under the visitor while they are still typing.
  FitFinder.prototype.runQuery = function (raw, scroll) {
    var handles = this.availableHandles();
    if (!raw || !normalize(raw)) {
      // Empty query: show every available card in its default order.
      this.render(handles, { mode: 'all', scroll: scroll });
      return;
    }

    var qTokens = significantTokens(tokenize(raw));
    var scored = handles.map(function (h) {
      return { handle: h, score: scoreCard(qTokens, this.cards[h] || {}) };
    }, this);
    scored.sort(function (a, b) { return b.score - a.score; });

    var matched = scored.filter(function (s) { return s.score > 0; }).map(function (s) { return s.handle; });

    if (matched.length) {
      this.render(matched, { mode: 'match', query: raw, count: matched.length, scroll: scroll });
    } else {
      var closeness = handles.map(function (h) {
        return { handle: h, score: closenessScore(qTokens, this.cards[h] || {}) };
      }, this);
      closeness.sort(function (a, b) { return b.score - a.score; });
      var closest = closeness.slice(0, 3).map(function (s) { return s.handle; });
      this.render(closest, { mode: 'closest', query: raw, scroll: scroll });
    }
  };

  FitFinder.prototype.render = function (orderedHandles, meta) {
    var self = this;

    // A chip's pressed state should only reflect the action that produced
    // the current results, never linger after a picker change or a cleared
    // search. The click handler re-applies "true" on the clicked chip
    // itself right after this call returns.
    this.chips.forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });

    var shown = {};
    orderedHandles.forEach(function (h, i) {
      var el = self.cardEls[h];
      if (!el) return;
      shown[h] = true;
      el.hidden = false;
      el.style.order = i;
    });
    Object.keys(this.cardEls).forEach(function (h) {
      if (!shown[h]) self.cardEls[h].hidden = true;
    });

    if (this.status) {
      var tmpl;
      if (meta.mode === 'all') {
        this.status.textContent = '';
      } else {
        tmpl = meta.mode === 'match' ? (this.copy.statusShowing || '') : (this.copy.statusClosest || '');
        this.status.textContent = tmpl.replace('{query}', meta.query || '').replace('{count}', meta.count || '');
      }
    }

    // On mobile the results grid can sit a long way below the search box
    // and picker; without this, tapping a chip or answering the picker
    // changes what is shown with no visible cue that anything happened.
    // "nearest" only scrolls when the grid is not already reasonably in
    // view, so it never yanks the page on desktop. Gated on meta.scroll so
    // this only ever fires for the chip/picker actions that set it, never
    // for the search box (typed input or its own submit).
    if (meta.scroll && meta.mode !== 'all' && this.grid && this.grid.scrollIntoView) {
      this.grid.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  function init() {
    var roots = document.querySelectorAll('[data-bm-finder]');
    roots.forEach(function (root) { new FitFinder(root); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

